// =============================================================================
// api/marketing.ts — typed client for the booking-campaign-send edge function
// =============================================================================
// One action-routed endpoint (see supabase/functions/booking-campaign-send).
// Every call is operator-JWT-authenticated; the server additionally enforces
// the booking_marketing permission (admin bypasses).
//
// Content model: per-locale variants (nl/en), each in 'blocks' mode
// (structured editor) or 'html' mode (pasted raw HTML with tokens). The
// server renders; `preview` returns the exact render for the editor iframe.
import { BOOKING_API_URL, supabase } from '../lib/supabase';

export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
export type CampaignLocale = 'nl' | 'en';
export type CampaignMode = 'blocks' | 'html';

export type CampaignBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'image'; url: string; alt?: string }
  // `icon` is a small image left of the label — the Google or Tripadvisor
  // mark on a review button. Both url and icon may be a {{token}}; the server
  // resolves them per recipient and drops the whole button when the url comes
  // back empty, which is how one review mail serves a venue that has only one
  // of the two links.
  | { type: 'button'; label: string; url: string; icon?: string };

export interface CampaignVariant {
  subject: string;
  preheader?: string | null;
  mode: CampaignMode;
  blocks?: CampaignBlock[];
  html?: string;
}

export type CampaignContent = Partial<Record<CampaignLocale, CampaignVariant>>;

export interface CampaignAudience {
  locale?: CampaignLocale;
  booked_since?: string; // YYYY-MM-DD
  // infra.location.id. Resolved server-side through booking.v_customer_locations,
  // so it matches customers who booked at the venue AND contacts imported for it
  // who have never booked (CRM loads). Undefined = every venue.
  location_id?: string;
  // Campaigns whose recipients to skip, so the same content can go out in
  // successive batches without anyone receiving it twice. Only people a listed
  // campaign actually sent to are excluded.
  exclude_campaign_ids?: string[];
  // Spacing rules (booking.fn_marketing_hold). A customer is "held" when they
  // got a marketing mail (campaign or post-visit mail) less than
  // recent_gap_days ago, or when they have never visited and a visit is still
  // ahead (until first_visit_delay_days after it). defer_held decides what
  // happens to them: false = not mailed by this campaign, true = mailed once
  // the hold ends. Absent = the server defaults (14 / true / 7 / false).
  recent_gap_days?: number;
  hold_first_visit?: boolean;
  first_visit_delay_days?: number;
  defer_held?: boolean;
}

export const AUDIENCE_DEFAULTS = {
  recent_gap_days: 14,
  hold_first_visit: true,
  first_visit_delay_days: 7,
  defer_held: false,
} as const;

export interface AudienceSummary {
  count: number; // whole audience
  ready: number; // may be mailed right away
  held_recent: number;
  held_first_visit: number;
  last_hold_until: string | null;
}

// What the spacing rules did to a started campaign.
export interface CampaignHeld {
  skipped_recently_mailed: number;
  skipped_upcoming_first_visit: number;
  waiting: number; // deferred, not due yet
  waiting_until: string | null; // when the last of them is due
}

export interface CampaignListRow {
  id: string;
  // Operator-facing label. Never appears in an e-mail; null falls back to the
  // subject, which matters once batches of the same mail share one subject.
  name: string | null;
  subject: string; // derived server-side from the default-locale variant
  locales: string[];
  default_locale: CampaignLocale;
  status: CampaignStatus;
  audience: CampaignAudience;
  recipient_count: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  test_sent_at: string | null;
  // Planned start (status 'scheduled'). The audience is counted and the
  // spacing rules applied at that moment, not when it was scheduled.
  scheduled_at: string | null;
  // Why a scheduled start fell back to draft (e.g. campaign_audience_empty).
  start_error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface CampaignDetail extends Omit<CampaignListRow, 'locales'> {
  content: CampaignContent;
  per_tick: number;
  cancelled_at: string | null;
  updated_at: string;
}

// Aggregated from booking.email_deliveries (ref = campaign id).
export interface CampaignStats {
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
}

export interface CampaignDraftInput {
  name: string | null;
  content: CampaignContent;
  default_locale: CampaignLocale;
  audience: CampaignAudience;
  per_tick: number;
}

export interface RenderedPreview {
  subject: string;
  html: string;
  text: string;
}

export interface MarketingError {
  error: string;
  message?: string;
}

export interface MarketingResult<T> {
  ok: boolean;
  data: T | null;
  error: MarketingError | null;
  status: number;
}

async function call<T>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<MarketingResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-campaign-send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ action, ...body }),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as MarketingError) ?? { error: 'unknown_error', message: `HTTP ${res.status}` },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, status: res.status, error: null };
}

export function listCampaigns() {
  return call<{ campaigns: CampaignListRow[]; from_address: string }>('list');
}

export function getCampaign(campaignId: string) {
  return call<{ campaign: CampaignDetail; stats: CampaignStats; held?: CampaignHeld }>('get', {
    campaign_id: campaignId,
  });
}

export function createCampaign(input: CampaignDraftInput) {
  return call<{ campaign_id: string }>('create', { ...input });
}

export function updateCampaign(campaignId: string, input: CampaignDraftInput) {
  return call<{ campaign_id: string }>('update', { campaign_id: campaignId, ...input });
}

// Copies content, audience and per_tick into a fresh draft. When the source
// actually mailed people, the server pre-adds it to the copy's exclusion list,
// which is the "send the rest" case.
export function duplicateCampaign(campaignId: string) {
  return call<{ campaign_id: string }>('duplicate', { campaign_id: campaignId });
}

export function deleteCampaign(campaignId: string) {
  return call<{ ok: true }>('delete', { campaign_id: campaignId });
}

export function audienceCount(audience: CampaignAudience) {
  return call<AudienceSummary>('audience_count', { audience });
}

// Server-rendered example (sample recipient, dummy unsubscribe link) — shows
// exactly what a real send would produce, including the auto-appended footer.
export function previewCampaign(
  content: CampaignContent,
  locale: CampaignLocale,
  defaultLocale: CampaignLocale,
) {
  return call<RenderedPreview>('preview', { content, locale, default_locale: defaultLocale });
}

// `to` empty = the logged-in operator's own address (server default).
// sample_names says whether the greeting used stand-in values because the
// address is not a customer; a real send always uses the customer's own name.
export function testSendCampaign(
  campaignId: string,
  locale: CampaignLocale,
  to?: string,
  plainSubject?: boolean,
) {
  return call<{ to: string; locale: string; sample_names: boolean; resend_id: string }>(
    'test_send',
    {
      campaign_id: campaignId,
      locale,
      ...(to ? { to } : {}),
      ...(plainSubject ? { plain_subject: true } : {}),
    },
  );
}

export function startCampaign(campaignId: string) {
  return call<{ recipient_count: number }>('start', { campaign_id: campaignId });
}

export function scheduleCampaign(campaignId: string, scheduledAt: string) {
  return call<{ scheduled_at: string }>('schedule', { campaign_id: campaignId, scheduled_at: scheduledAt });
}

// Scheduled -> draft, so it can be edited or sent right away.
export function unscheduleCampaign(campaignId: string) {
  return call<{ ok: true }>('unschedule', { campaign_id: campaignId });
}

export function cancelCampaign(campaignId: string) {
  return call<{ ok: true }>('cancel', { campaign_id: campaignId });
}
